/** Read-only compatibility for agreements signed before guided signing v2. */
import { usesHomeImprovementAgreement } from './esrContractCompany'

type Customer = Readonly<Record<string, unknown>> | null | undefined
const filled = (value: unknown) => typeof value === 'string' && !!value.trim()
// MSE, CE and ESR (13376550851) sign through the same home_improvement_* fields.
const contractFields = (company?: string): string[] =>
  usesHomeImprovementAgreement(company)
    ? ['home_improvement_sign_owner1']
    : ['FE', 'HC', 'Wildcat', 'OTHER_CASH', 'OTHER_FIN'].includes(company || '')
    ? ['security_contract_sign_customer']
    : []

/** Primary customer signatures define a signed legacy agreement, not an email receipt.
 * Equipment initials / cancellation / representative-only signatures do not qualify.
 */
export function legacyAgreementSigned(
  customer: Customer,
  company?: string,
): boolean {
  if (!customer || customer['signing_flow_version'] === 'v2') return false
  const fields = contractFields(company)
  if (
    usesHomeImprovementAgreement(company) &&
    filled(customer['home_improvement_owner2_name'])
  )
    fields.push('home_improvement_sign_owner2')
  return fields.length > 0 && fields.every((field) => filled(customer[field]))
}

/** Customer's primary contract date, then the existing explicit report date.
 * Require matching signature for contract dates; never cancellation, completion,
 * today's date or another company's contract. Preserve report-date fallback.
 */
export function coverSigningStatus(
  customer: Customer,
  company?: string,
): { signed: boolean; date: number | null } {
  if (!customer) return { signed: false, date: null }
  const signedFields = contractFields(company).filter((field) =>
    filled(customer[field]),
  )
  for (const field of signedFields) {
    const date = customer[field + '_date']
    if (
      typeof date === 'number' &&
      Number.isFinite(date) &&
      date > 0 &&
      date < 8.64e15
    )
      return { signed: true, date }
  }
  const reportDate = customer['report_company_signature_date']
  if (
    typeof reportDate === 'number' &&
    Number.isFinite(reportDate) &&
    reportDate > 0 &&
    reportDate < 8.64e15
  )
    return { signed: true, date: reportDate }
  return {
    signed:
      signedFields.length > 0 || filled(customer['report_company_signature']),
    date: null,
  }
}
